
import { sendMessageToOpenAI } from './openaiService';

interface ExtractedBusinessData {
  businessName: string;
  businessType: string;
  targetAudience: string;
  uniqueSellingPoints: string[];
  readyForNotion: boolean;
}

/**
 * Extracts structured business data from a conversation history using OpenAI
 */
export const extractBusinessData = async (messages: Array<{role: string, content: string}>, apiKey: string): Promise<ExtractedBusinessData> => {
  try {
    console.log('Extracting business data from conversation');
    
    // Create a system message that instructs GPT to extract business information
    const systemMessage = {
      role: 'system',
      content: `Extract business information from the conversation. Parse the conversation and identify:
      1. Business name
      2. Business type/industry
      3. Target audience
      4. Unique selling points (at least 1, maximum 5)
      5. Whether there is enough information to create a Notion business plan
      
      Return ONLY a JSON object with this structure:
      {
        "businessName": "string or null if unknown",
        "businessType": "string or null if unknown",
        "targetAudience": "string or null if unknown",
        "uniqueSellingPoints": ["array of strings or empty array if unknown"],
        "readyForNotion": boolean (true if businessName, businessType, and at least 1 uniqueSellingPoint are provided)
      }`
    };

    // Create the full message array with our extraction instructions
    const promptMessages = [
      systemMessage,
      ...messages
    ];

    // Get the extraction response from OpenAI
    const extractionResponse = await sendMessageToOpenAI(promptMessages, apiKey);
    
    console.log('Extraction response:', extractionResponse);
    
    // Parse the JSON response
    try {
      const parsedData = JSON.parse(extractionResponse);
      
      return {
        businessName: parsedData.businessName || 'Your Business',
        businessType: parsedData.businessType || 'Unknown',
        targetAudience: parsedData.targetAudience || 'General audience',
        uniqueSellingPoints: Array.isArray(parsedData.uniqueSellingPoints) ? 
          parsedData.uniqueSellingPoints : [],
        readyForNotion: !!parsedData.readyForNotion
      };
    } catch (parseError) {
      console.error('Error parsing extraction response:', parseError);
      console.log('Raw response:', extractionResponse);
      
      return {
        businessName: 'Your Business',
        businessType: 'Unknown',
        targetAudience: 'General audience',
        uniqueSellingPoints: [],
        readyForNotion: false
      };
    }
  } catch (error) {
    console.error('Error extracting business data:', error);
    
    return {
      businessName: 'Your Business',
      businessType: 'Unknown',
      targetAudience: 'General audience',
      uniqueSellingPoints: [],
      readyForNotion: false
    };
  }
};

/**
 * Determines if a conversation is ready for Notion plan creation
 * based on AI analysis of the conversation
 */
export const shouldOfferNotionPlan = async (messages: Array<{role: string, content: string}>, apiKey: string): Promise<boolean> => {
  try {
    console.log('Checking if conversation is ready for Notion plan');
    
    // If there are less than 4 messages, it's probably too early
    if (messages.length < 4) {
      console.log('Too few messages, not ready for Notion plan');
      return false;
    }
    
    // Create a system message that instructs GPT to determine if we're ready for Notion
    const systemMessage = {
      role: 'system',
      content: `Analyze the conversation and determine if enough information has been gathered to create a Notion business plan. 
      We need:
      1. Some indication of business type/industry
      2. At least one unique selling point
      3. Some information about target audience or business goals
      
      Return ONLY "YES" if there is enough information, or "NO" if more information is needed.`
    };

    // Create the full message array with our analysis instructions
    const promptMessages = [
      systemMessage,
      ...messages
    ];

    // Get the analysis response from OpenAI
    const analysisResponse = await sendMessageToOpenAI(promptMessages, apiKey);
    
    console.log('Notion readiness analysis response:', analysisResponse);
    
    // Check if the response indicates we're ready
    const normalizedResponse = analysisResponse.trim().toUpperCase();
    const isReady = normalizedResponse.includes('YES');
    
    console.log('Is ready for Notion plan:', isReady);
    return isReady;
  } catch (error) {
    console.error('Error analyzing conversation readiness:', error);
    return false;
  }
};
